import { useCallback, useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CsrAPI } from "../apis/CsrAPI";
import { SCS_STATUS } from "../utils/Constants";
import { getApiErrorMessage } from "../utils/apiError";
import FormatDate from "../utils/FormatDate";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";

// The render runs in the background, so the status endpoint is polled until it
// stops answering 202.
const POLL_INTERVAL_MS = 5000;

// Owns everything behind the Generate / Download / Generate again buttons on
// the SCS file page: the latest render's status, its traffic-lights report and
// the polling loop that carries one to the other.
//
// Statuses the backend can report are `ready`, `finished`, `render available`
// and `error`, but the HTTP code is the reliable done/not-done signal — 202
// means still rendering, 200 means the run has finished — so that is what
// drives the state machine, with the body's `error` status layered on top.
const useScsRender = (summaryId, { onForbidden } = {}) => {
  const [mainStatus, setMainStatus] = useState(SCS_STATUS.inProgress);
  const [requestId, setRequestId] = useState("");
  const [lastGeneratedAt, setLastGeneratedAt] = useState("");
  const [trafficLights, setTrafficLights] = useState([]);
  const [downloading, setDownloading] = useState(false);
  // Bumped whenever a render completes, so the history dropdown knows to
  // re-fetch its versions.
  const [versionsToken, setVersionsToken] = useState(0);

  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const onForbiddenRef = useRef(onForbidden);
  onForbiddenRef.current = onForbidden;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const handleForbidden = useCallback(() => {
    onForbiddenRef.current?.();
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!summaryId) return;
    stopPolling();

    try {
      const result = await CsrAPI.getSafetySummaryRenderStatus({ summaryId });
      if (!mountedRef.current) return;

      if (result.requestId) setRequestId(String(result.requestId));

      if (result.httpStatus === 202) {
        setMainStatus(SCS_STATUS.generating);
        pollRef.current = setTimeout(fetchStatus, POLL_INTERVAL_MS);
        return;
      }

      if (result.status === "error") {
        setMainStatus(SCS_STATUS.errored);
        setTrafficLights([]);
        return;
      }

      setMainStatus(SCS_STATUS.ready);
      setLastGeneratedAt(
        result.finishedAt ? FormatDate(result.finishedAt) : ""
      );
      // `trafficLights` is the documented key; `message` is what the CSR
      // status endpoint uses for the same report, so accept either.
      const report = result.trafficLights ?? result.message;
      setTrafficLights(Array.isArray(report) ? report : []);
      setVersionsToken(token => token + 1);
    } catch (error) {
      if (!mountedRef.current) return;

      if (error.response?.status === 403) {
        handleForbidden();
        return;
      }
      // 404 simply means nothing has been rendered for this summary yet —
      // the page then offers "Generate SCS file".
      if (error.response?.status === 404) {
        setMainStatus(SCS_STATUS.inProgress);
        setTrafficLights([]);
        return;
      }
      if (isProduction) {
        Sentry.captureException(error);
      }
      setMainStatus(SCS_STATUS.errored);
    }
  }, [summaryId, stopPolling, handleForbidden]);

  // Pick up the status of the latest render as soon as the page opens.
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const generate = useCallback(async () => {
    if (!summaryId) return;

    stopPolling();
    setTrafficLights([]);
    setLastGeneratedAt("");
    setMainStatus(SCS_STATUS.generating);

    try {
      const result = await CsrAPI.renderSafetySummary({ summaryId });
      if (!mountedRef.current) return;
      if (result.requestId) setRequestId(String(result.requestId));
      // The render is queued, not finished — start following it.
      pollRef.current = setTimeout(fetchStatus, POLL_INTERVAL_MS);
    } catch (error) {
      if (!mountedRef.current) return;

      if (error.response?.status === 403) {
        handleForbidden();
        return;
      }

      if (isProduction) {
        Sentry.captureException(error);
      }

      if (!error?.response) {
        toast.error("No Server Response");
      } else if (error.response.status === 401) {
        toast.error("Invalid user credentials");
      } else if (error.response.status === 404) {
        toast.error("Summary not found");
      } else if (error.response.status === 409) {
        toast.error(
          getApiErrorMessage(
            error,
            "SCS can't be generated yet. Make sure every uploaded file has finished processing and all required sections are complete."
          )
        );
      } else {
        toast.error("Failed to start SCS generation");
      }

      // The render never started, so fall back to whatever the summary's real
      // state is rather than leaving the page stuck on "Generating".
      fetchStatus();
    }
  }, [summaryId, stopPolling, fetchStatus, handleForbidden]);

  const download = useCallback(
    async fileName => {
      if (!summaryId) return;

      setDownloading(true);
      try {
        const result = await CsrAPI.getSafetySummaryDownloadUrl({ summaryId });

        if (result.httpStatus === 202 || !result.downloadUrl) {
          toast.info("SCS is still being generated. Please try again shortly.");
          // A download attempt mid-render means our status is stale.
          fetchStatus();
          return;
        }

        const blob = await CsrAPI.downloadPresignedFile({
          url: result.downloadUrl,
        });
        saveAs(blob, fileName);
        toast.success("SCS downloaded successfully!");
      } catch (error) {
        if (error.response?.status === 403) {
          handleForbidden();
          return;
        }

        if (isProduction) {
          Sentry.captureException(error);
        }

        if (!error?.response) {
          toast.error("No Server Response");
        } else if (error.response.status === 401) {
          toast.error("Invalid user credentials");
        } else if (error.response.status === 404) {
          toast.error("No generated SCS file is available to download.");
          fetchStatus();
        } else {
          toast.error("Failed to download SCS");
        }
      } finally {
        if (mountedRef.current) setDownloading(false);
      }
    },
    [summaryId, fetchStatus, handleForbidden]
  );

  return {
    mainStatus,
    requestId,
    lastGeneratedAt,
    trafficLights,
    downloading,
    versionsToken,
    generate,
    download,
    refreshStatus: fetchStatus,
  };
};

export default useScsRender;
